'use client';

import { AnalyticsContext } from '@carvana/eventable-clickstream';
import isPropValid from '@emotion/is-prop-valid';
import React, { useContext, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

export type OwnProps = React.HTMLProps<HTMLElement> & {
  callback?(): void;
  delay?: number;
  element?: React.ElementType;
  event: {
    customAttributes?: Record<string, any>;
    eventName: string;
  };
  shouldTrack?: boolean;
  threshold?: number;
  trackOnce?: boolean;
};

export default function TrackWrapper({
  callback,
  children,
  delay = 1000,
  element: Element = 'div',
  event: { eventName, customAttributes },
  shouldTrack = true,
  threshold = 0.5,
  trackOnce = false,
  ...propsForElement
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const [ref, inView] = useInView({ threshold });
  const didTrack = useRef(false);

  // Filter props to only those valid for HTML elements
  const props = Object.entries(propsForElement).reduce<Record<string, any>>(
    (acc, [key, value]) => {
      if (isPropValid(key)) {
        acc[key] = value;
      }

      return acc;
    },
    {}
  );

  useEffect(() => {
    if (!inView || !shouldTrack) return;

    const t = window.setTimeout(() => {
      if (didTrack.current && trackOnce) return;

      trackEvent(eventName, customAttributes);
      callback?.();
      didTrack.current = true;
    }, delay);

    return () => {
      clearTimeout(t);
    };
  }, [
    callback,
    customAttributes,
    delay,
    eventName,
    inView,
    shouldTrack,
    trackEvent,
    trackOnce
  ]);

  return (
    <Element ref={ref} {...props}>
      {children}
    </Element>
  );
}
